import { createApp } from 'vue'
import './style.css'
import App from './App.vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import ComsPlugin from './plugins/coms'
import bus from './plugins/bus'
import store from './store'
import vuetify from './plugins/vuetify'
import FusePlugin from './plugins/fuseSearch'
import router from './router'
import PortalVue from 'portal-vue'
import VueSlideUpDown from 'vue-slide-up-down'

axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;

const app = createApp(App)
app.config.globalProperties.$bus = bus
app.use(VueAxios, axios)
app.use(router)
app.use(store)
app.use(vuetify)
app.use(ComsPlugin)
app.use(FusePlugin)
app.use(PortalVue)
app.component('VueSlideUpDown', VueSlideUpDown)

Object.defineProperty(app.config.globalProperties, '$isDark', {
  get() {
    return vuetify.theme.global.current.value.dark
  }
})


const PASSWORD_STRENGTH = 2
export { PASSWORD_STRENGTH }

/**
 * This function runs after a user is successfully authenticated.
 * It commits the initial user data and then dispatches actions to get more details.
 * @param {object} fullUserObject - The full user object from the 'authenticate' event.
 */
async function onLoginSuccess(fullUserObject) {
  // The token from the 'authenticate' event is the source of truth.
  store.commit('user/setUserData', fullUserObject)
  store.commit('setAuthentication', true)
  
  const savedTheme = localStorage.getItem(`theme-${fullUserObject.id}`)
  if (savedTheme) {
    vuetify.theme.change(savedTheme)
  } else {
    vuetify.theme.change('light')
  }

  try {
    // Now that the state is set, we can safely dispatch other actions.
    // The getUserData action will fetch more detailed info from the backend.
    if (store.getters.role === 'user') {
      // We can get the customer_id from the object we just received.
      await store.dispatch('customer/getCustomer', fullUserObject.customer_id)
      store.dispatch('customer/checkForPowerPoints')
      store.dispatch('customer/checkForReferenceFiles')
      store.dispatch('customer/checkForFileSharing')
      store.commit('user/setJustLoggedIn', true);
    }
  } catch (error) {
    console.error("Failed to fetch additional user/customer data after login:", error)
  }
}

/**
 * Sets up all the application's event listeners and initial state.
 */
function bootstrapApp() {
  store.commit('navigation/reset')
  store.commit('setConnected', false)

  // This listener updates the app's state when the socket connects.
  app.config.globalProperties.$coms.util.connectionEvent((data) => {
    store.commit('setConnected', true)
    // This part handles auto-login if the user already has a valid session token.
    if (data.isAuthenticated && !store.state.authenticated) {
      onLoginSuccess(app.config.globalProperties.$coms.util.getAuthToken())
    }
  })

  // This listener handles what happens when the socket disconnects.
  app.config.globalProperties.$coms.util.disconnectEvent(() => {
    store.commit('setConnected', false)
  })

  // This listener handles what happens AFTER a user actively logs in.
  app.config.globalProperties.$coms.authentication.authenticated(async (token) => {
    if (!store.state.authenticated) { // Prevent running twice if event fires multiple times
      await onLoginSuccess(token)
    }
  })

  app.config.globalProperties.$coms.authentication.deauthenticated(() => {
    store.commit('setAuthentication', false)
    store.commit('user/clear', null) // Clear user data
    router.push({ name: 'Login' })
  })

    // This listener handles raw messages from the server, like being logged out from another browser.
  app.config.globalProperties.$coms.util.rawEvent(data => {
    if (data === 'User authenticated on another browser') {
      store.commit('setDeauthenticatedEvent', {
        message: 'You were logged out because someone logged into your account from another browser',
      })
    }
  })
  // This watcher handles all post-login navigation logic.
  store.watch(
    () => store.getters.authenticated,
    (isAuthenticated) => {
      if (isAuthenticated) {
        if (store.state.user.resetFlag && !store.getters.isPoser) {
          router.push({ name: 'Reset' })
        } else {
          const initialPath = store.state.initialPath
          if (initialPath && initialPath !== '/login' && initialPath != '/') {
            router.replace(initialPath)
            store.commit('setInitialPath', null)
          } else {
            // Redirect to a default home page after login
            router.push({ name: 'Dashboard' })
          }
        }
      }
    }
  )

  app.mount('#app')
}

bootstrapApp()